// INN-6: Mood Picker — one-click section mood themes with undo
// 5 mood cards + Custom indicator + 10s revert window
import { useCallback, useEffect, useRef, useState } from "react";
import { Undo2 } from "lucide-react";
import { type MoodId, MOOD_THEMES } from "./mood-themes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MoodPickerProps {
  currentMoodId: MoodId | null;
  onApply: (moodId: MoodId | null) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REVERT_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MoodPicker({ currentMoodId, onApply }: MoodPickerProps) {
  const [previousMoodId, setPreviousMoodId] = useState<MoodId | null>(null);
  const [canRevert, setCanRevert] = useState(false);
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeMood = currentMoodId;

  // Clear revert timer on unmount
  useEffect(() => {
    return () => {
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    };
  }, []);

  const handleApply = useCallback(
    (moodId: MoodId) => {
      if (!MOOD_THEMES.some((theme) => theme.id === moodId)) return;

      // Store previous mood for undo
      setPreviousMoodId(currentMoodId);
      setCanRevert(true);

      // Clear existing timer
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);

      // Start new revert window
      revertTimerRef.current = setTimeout(() => {
        setCanRevert(false);
        setPreviousMoodId(null);
        revertTimerRef.current = null;
      }, REVERT_TIMEOUT_MS);

      onApply(moodId);
    },
    [currentMoodId, onApply],
  );

  const handleRevert = useCallback(() => {
    if (revertTimerRef.current) {
      clearTimeout(revertTimerRef.current);
      revertTimerRef.current = null;
    }

    onApply(previousMoodId);
    setPreviousMoodId(null);
    setCanRevert(false);
  }, [onApply, previousMoodId]);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-[var(--text-sm)] font-semibold text-[var(--color-foreground)]">
          Mood
        </span>
        {canRevert && (
          <button
            type="button"
            onClick={handleRevert}
            className="flex items-center gap-1 px-2 py-1 rounded text-[var(--text-xs)] font-medium text-[var(--color-destructive)] hover:bg-[var(--color-surface)] transition-colors"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            <Undo2 size={12} />
            Revert
          </button>
        )}
      </div>

      {/* Mood cards */}
      <div className="flex gap-2">
        {MOOD_THEMES.map((theme) => {
          const isActive = activeMood === theme.id;

          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => handleApply(theme.id)}
              className="flex-1 flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all"
              style={{
                borderColor: isActive ? "var(--color-primary)" : "var(--color-border)",
                backgroundColor: isActive ? "var(--color-primary-light)" : "var(--color-card)",
                transitionDuration: "var(--duration-fast)",
              }}
              aria-pressed={isActive}
              title={theme.description}
            >
              {/* Color swatch preview */}
              <div className="flex gap-0.5">
                <span
                  className="block w-5 h-5 rounded-sm border border-[var(--color-border)]"
                  style={{ backgroundColor: theme.previewColor }}
                />
                <span
                  className="block w-5 h-5 rounded-sm border border-[var(--color-border)]"
                  style={{ backgroundColor: theme.previewAccent }}
                />
              </div>
              <span
                className="text-[10px] font-medium leading-none"
                style={{
                  color: isActive ? "var(--color-primary)" : "var(--color-muted-foreground)",
                }}
              >
                {theme.name}
              </span>
            </button>
          );
        })}

        {/* Custom indicator */}
        <div
          className="flex-1 flex flex-col items-center gap-1.5 p-2 rounded-lg border"
          style={{
            borderColor: activeMood === null ? "var(--color-primary)" : "var(--color-border)",
            backgroundColor: activeMood === null ? "var(--color-primary-light)" : "var(--color-card)",
            opacity: activeMood === null ? 1 : 0.5,
          }}
        >
          <div className="flex gap-0.5">
            <span className="block w-5 h-5 rounded-sm border border-dashed border-[var(--color-muted-foreground)]" />
            <span className="block w-5 h-5 rounded-sm border border-dashed border-[var(--color-muted-foreground)]" />
          </div>
          <span
            className="text-[10px] font-medium leading-none"
            style={{
              color: activeMood === null ? "var(--color-primary)" : "var(--color-muted-foreground)",
            }}
          >
            Custom
          </span>
        </div>
      </div>
    </div>
  );
}
